import { useAuth } from '@clerk/clerk-react';

export interface ApiClient {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
  put: <T>(path: string, body?: unknown) => Promise<T>;
  upload: <T>(path: string, form: FormData) => Promise<T>;
  download: (path: string, fallbackName: string) => Promise<void>;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body: any) {
    super(message);
  }
}

/** Hook returning an API client that injects the Clerk session token. */
export function useApi(): ApiClient {
  const { getToken } = useAuth();

  async function send(method: string, path: string, body?: BodyInit, json = false): Promise<Response> {
    const token = await getToken();
    const res = await fetch(path, {
      method,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(json ? { 'Content-Type': 'application/json' } : {}),
      },
      body,
    });
    if (!res.ok) {
      const parsed = await res.json().catch(() => null);
      throw new ApiError(res.status, parsed?.error ?? `${res.status} ${res.statusText}`, parsed);
    }
    return res;
  }

  const jsonBody = (b: unknown) => (b === undefined ? undefined : JSON.stringify(b));

  return {
    get: async (path) => (await send('GET', path)).json(),
    post: async (path, body) => (await send('POST', path, jsonBody(body), body !== undefined)).json(),
    put: async (path, body) => (await send('PUT', path, jsonBody(body), body !== undefined)).json(),
    upload: async (path, form) => (await send('POST', path, form)).json(),
    download: async (path, fallbackName) => {
      const res = await send('GET', path);
      const cd = res.headers.get('Content-Disposition') ?? '';
      const name = /filename="?([^"]+)"?/.exec(cd)?.[1] ?? fallbackName;
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    },
  };
}
